import { AbstractComponent } from '@/utils/sections/abstract-component';
import { register } from '@/utils/sections/registration';
import { captureException } from '@sentry/browser';

export class NewsIndex extends AbstractComponent {
  init() {
    this.wrapEl = this.el.querySelector('[data-news-wrap]');
    this.itemsEl = this.el.querySelector('[data-news-items]');
    this.loadMoreEl = this.el.querySelector('[data-news-load-more]');
    this.isLoading = false;
    this.bar = this.el.querySelector('[data-bar]');
    this.filterTags = this.el.querySelectorAll('[data-filter-tag]');
    this.filterSelects = this.el.querySelectorAll('[data-filter-select]');
    this.filterClearEl = this.el.querySelector('[data-filter-clear]');
    this.emptyMessageEl = this.el.querySelector('[data-news-empty]');

    this.bindEvents();
  }

  bindEvents() {
    this.loadMoreEl.addEventListener('click', (ev) => {
      ev.preventDefault();
      this.loadNextPage();
    });

    this.filterClearEl.addEventListener('click', (e) => {
      e.preventDefault();
      this.updateOptions({ filter: {} });
      this.filterSelects.forEach((el) => {
        el.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { passiveClear: true } }));
      });
      this.filter();
    });

    this.filterSelects.forEach((el) => {
      const select = el.querySelector('select');

      el.addEventListener('change', (e) => {
        const passiveClear = e.detail?.passiveClear || false;

        if (passiveClear) {
          this.el.querySelectorAll('[data-value]').forEach((opt) => {
            opt.classList.remove('is-active');
          });
          const select = el.querySelector('select');
          for (let i = 0; i < select.options.length; i++) {
            select.options[i].selected = false;
          }
        }

        if (!passiveClear) {
          const name = el.dataset.filterSelect;
          const values = Array.from(select.selectedOptions).map((option) => option.value);
          const currentFilter = this.options.filter || {};
          currentFilter[name] = values;

          this.updateOptions({
            filter: currentFilter,
          });
        }

        this.filterTags.forEach((tag) => {
          tag.classList.toggle('is-active', Object.values(this.options.filter).flat().includes(tag.dataset.filterTag));
        });

        if (!passiveClear) {
          this.filter();
        }
      });
    });

    this.filterTags.forEach((tag) => {
      tag.addEventListener('click', (e) => {
        const el = this.el.querySelector(`[data-value="${e.target.dataset.filterTag}"]`);
        el.classList.remove('is-active');
        tag.classList.remove('is-active');

        const list = el.closest('ul');
        const values = [...list.children].filter((el) => el.classList.contains('is-active')).map((el) => el.dataset.value);
        const select = el.closest('div').querySelector('select');
        const options = select.options;

        for (let i = 0; i < options.length; i++) {
          options[i].selected = values.includes(options[i].value);
        }

        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /**
   * If we can load next page - load it
   */
  loadNextPage(replace = false) {
    if (this.isLoading) {
      return;
    }

    if (!this.canLoadNextPage() && !replace) {
      return;
    }

    this.load(
      {
        page: this.options.currentPage + 1,
        filter: this.options.filter || {},
      },
      replace
    );
  }

  updateOptions(options) {
    this.options = {
      ...this.options,
      ...options,
    };
  }

  setLoading(loading) {
    this.isLoading = loading;
    this.wrapEl.classList.toggle('is-loading', this.isLoading);
  }

  /**
   * when filtering we need to reset the options and load the first page
   */
  filter() {
    this.bar.classList.toggle('is-active', this.hasFilters());
    if (this.isLoading) {
      return;
    }

    this.updateOptions({
      currentPage: 0,
    });

    this.loadNextPage(true);
  }

  load(params, replace = false) {
    this.setLoading(true);
    return this.doRequest(params)
      .then((response) => {
        this.updateOptions(response.options || {});

        if (replace) {
          this.itemsEl.innerHTML = response.html;
        } else {
          this.itemsEl.insertAdjacentHTML('beforeend', response.html);
        }

        this.toggleAnnouncement();
        this.toggleEmptyMessage();
        // maybe hide the load more button
        this.toggleLoadMoreCta();
      })
      .then(() => this.setLoading(false));
  }

  doRequest(params) {
    return fetch('/api/v1/news/load-more', {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
      },
    })
      .then((response) => response.json())
      .then((data) => data)
      .catch((err) => captureException(err));
  }

  toggleLoadMoreCta() {
    this.loadMoreEl.classList.toggle('is-hidden', this.options.currentPage >= this.options.totalPages);
  }

  toggleEmptyMessage() {
    const isEmpty = !this.itemsEl.innerHTML.trim();

    this.itemsEl.classList.toggle('is-hidden', isEmpty);
    this.emptyMessageEl.classList.toggle('is-active', isEmpty);
  }

  toggleAnnouncement() {
    this.el.classList.toggle('has-filters', this.hasFilters());
  }

  hasFilters() {
    return Object.values(this.options.filter || {}).flat().length > 0;
  }

  canLoadNextPage() {
    return this.options.currentPage < this.options.totalPages;
  }
}

register('news-index', NewsIndex);
